/**
 * Item search, run against the local catalog instead of the API.
 *
 * This replaces a request per keystroke — the third-largest source of Supabase
 * egress (see docs/guide/egress-plan.md). The contract is `SearchService`'s, so
 * the caller cannot tell which side answered: same filters in, `{results, total}`
 * out, same ordering and paging.
 *
 * ## What is reproduced faithfully, and what is not
 *
 * The filters are *correctness*: a level cap or a stat floor that disagrees with
 * the server would show items the build cannot use. Those are ported exactly —
 * level bounds, type ids, explicit id lists, and the eq/min/max rules over
 * `effects_map`, including the special case where `eq: 0` also matches an absent
 * key.
 *
 * Name matching is deliberately *stricter* than the server, which is the one
 * place the two are meant to diverge. A lenient matcher is not a nicer matcher:
 * every word it lets through is one the reader has to rule out by hand.
 *
 * The rule is "a typo is an edit you did not mean to make", so tolerance is
 * earned by length — one edit per `CHARS_PER_EDIT` characters typed, measured
 * against the start of a word rather than the whole of it, so a word you are
 * halfway through still matches. Four letters buy nothing, which is the point:
 * at four letters nearly everything is one edit from something.
 *
 * Two looser rules came before this one and drew the same complaint. uFuzzy
 * allowed a skipped character between every pair, so "cire" ran c-u-i-v-r-e and
 * found "Clochecuivre". The server's own `word_similarity >= 0.3` is barely
 * tighter at that length — "cire" against "claire" scores 0.33 — so porting it
 * faithfully returned "Panoplie Claire" instead. Neither is a match anyone
 * typing "cire" was asking for.
 */

import { TYPES, CAT } from '../core/buildUtils';

/**
 * Characters that buy one edit of tolerance. Five means a four-letter token gets
 * none at all and has to match outright, "boreale" survives a slip, and a long
 * compound survives two. Lower it and short tokens start dragging in their
 * neighbours; raise it and a real typo in a middling word stops matching.
 */
const CHARS_PER_EDIT = 5;
/** Ceiling on that budget: past two edits it is a different word, not a typo. */
const MAX_EDITS = 2;
/** Mirrors the server's default page size. */
const DEFAULT_LIMIT = 18;

/** Strip accents and case, the way `unaccent()` + ILIKE do server-side. */
export function normalizeText(value) {
    return String(value || '')
        .normalize('NFD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase();
}

function tokenize(name) {
    return String(name || '').trim().split(/\s+/).filter(Boolean);
}

export function localizedName(nameField, lang) {
    if (!nameField) return '';
    if (typeof nameField === 'string') return nameField;
    return nameField[lang] || nameField.fr || nameField.en || '';
}

/**
 * Haystacks are built once per (catalog, lang) and reused across keystrokes —
 * rebuilding 3,745 normalized strings on every character was the one thing that
 * made this slower than the network call it replaces.
 */
const haystackCache = new WeakMap();

function haystackFor(catalog, lang) {
    let byLang = haystackCache.get(catalog);
    if (!byLang) {
        byLang = new Map();
        haystackCache.set(catalog, byLang);
    }
    let entry = byLang.get(lang);
    if (!entry) {
        // The server also matches an item whose *set* name matches, so the
        // haystack is "item name + set name" per item.
        const names = catalog.items.map((item) => {
            const own = localizedName(item.name, lang);
            const set = localizedName(item.item_set_name, lang);
            return normalizeText(set ? `${own} ${set}` : own);
        });
        // Split words are cached alongside, for the same reason, and built on
        // the keystroke that first needs them rather than on catalog load.
        entry = { names, words: null };
        byLang.set(lang, entry);
    }
    return entry;
}

/**
 * Rows of the edit-distance table, allocated once. Two would do for plain
 * Levenshtein; the third is what makes a transposition cost one edit instead of
 * two, and a transposition is the commonest slip there is ("boraele").
 */
const MAX_WORD_LEN = 64;
const dpRowA = new Int32Array(MAX_WORD_LEN + 2);
const dpRowB = new Int32Array(MAX_WORD_LEN + 2);
const dpRowC = new Int32Array(MAX_WORD_LEN + 2);

/**
 * True when `needle` is within `budget` edits of some *prefix* of `word`.
 *
 * Prefix, not the whole word, because the box is searched while it is being
 * typed: "clochcuiv" is a typo mid-word, and waiting for the whole of
 * "clochecuivre" before forgiving it would make this feel like a regression.
 *
 * The table is banded — only columns within `budget` of the current row can
 * still come in under it — so the cost is O(len x budget) rather than O(len^2),
 * and a row that never dips to `budget` ends it early.
 */
function withinEditBudget(needle, word, budget) {
    const n = needle.length;
    const m = word.length;
    if (m > MAX_WORD_LEN || m + budget < n) return false;

    let prev2 = dpRowA;
    let prev = dpRowB;
    let cur = dpRowC;
    // Row 0 is the identity: a prefix match is free to stop early, so dropping
    // the word's tail costs nothing and only its head costs edits.
    for (let j = 0; j <= m; j += 1) prev[j] = j;

    for (let i = 1; i <= n; i += 1) {
        const lo = Math.max(1, i - budget);
        const hi = Math.min(m, i + budget);
        cur[lo - 1] = i;
        let rowBest = i;
        for (let j = lo; j <= hi; j += 1) {
            const cost = needle[i - 1] === word[j - 1] ? 0 : 1;
            let best = Math.min(
                prev[j] + 1,        // drop a character the needle has
                cur[j - 1] + 1,     // add one it is missing
                prev[j - 1] + cost, // type the wrong one
            );
            if (i > 1 && j > 1 && needle[i - 1] === word[j - 2] && needle[i - 2] === word[j - 1]) {
                best = Math.min(best, prev2[j - 2] + 1); // type two in the wrong order
            }
            cur[j] = best;
            if (best < rowBest) rowBest = best;
        }
        if (hi < m) cur[hi + 1] = budget + 1;
        if (rowBest > budget) return false;
        const spare = prev2;
        prev2 = prev;
        prev = cur;
        cur = spare;
    }
    // The word may run on past the needle, so any column of the last row that
    // came in under budget is a prefix that matches.
    for (let j = Math.max(0, n - budget); j <= Math.min(m, n + budget); j += 1) {
        if (prev[j] <= budget) return true;
    }
    return false;
}

/**
 * Words are cached with the names — re-splitting 3,745 strings per keystroke is
 * the sort of thing that made local search slower than the request it replaced.
 */
function wordsFor(entry) {
    if (!entry.words) {
        entry.words = entry.names.map((n) => n.split(/\s+/).filter(Boolean));
    }
    return entry.words;
}

function matchesName(catalog, lang, name) {
    const tokens = tokenize(name);
    if (!tokens.length) return null;

    const entry = haystackFor(catalog, lang);
    const { names } = entry;
    let surviving = null;

    // Every token must match, in any order — the server ANDs its per-token
    // clauses, so an item matching only one of two words is not a result.
    for (const token of tokens) {
        const needle = normalizeText(token);
        const hits = new Set();
        for (let i = 0; i < names.length; i += 1) {
            if (names[i].includes(needle)) hits.add(i);
        }
        // Union, not fallback: an item an edit away is still a result when other
        // items matched outright, the way the server's OR has it.
        const budget = Math.min(MAX_EDITS, Math.floor(needle.length / CHARS_PER_EDIT));
        if (budget > 0) {
            const words = wordsFor(entry);
            for (let i = 0; i < names.length; i += 1) {
                if (hits.has(i)) continue;
                for (const word of words[i]) {
                    if (withinEditBudget(needle, word, budget)) { hits.add(i); break; }
                }
            }
        }
        surviving = surviving === null ? hits : new Set([...surviving].filter((i) => hits.has(i)));
        if (!surviving.size) return surviving;
    }
    return surviving;
}

function passesStats(item, stats) {
    if (!stats) return true;
    const map = item.effects_map || {};
    for (const [charId, rules] of Object.entries(stats)) {
        if (!/^\d+$/.test(charId)) continue;
        const key = String(Number(charId));
        const raw = map[key] ?? map[Number(key)];
        const has = raw !== undefined && raw !== null;
        const value = has ? Number(raw) : 0;
        if (rules.eq !== undefined) {
            // eq:0 also matches an item that simply has no such line.
            if (rules.eq === 0 ? has && value !== 0 : value !== rules.eq) return false;
        }
        if (rules.min !== undefined && value < rules.min) return false;
        if (rules.max !== undefined && value > rules.max) return false;
    }
    return true;
}

/** Sets carry their members; items do not. The only structural tell either way. */
const isSetRow = (row) => Array.isArray(row.item_ids);

/**
 * The id ties break on — `COALESCE(max_item_id, id)` in the unified endpoint's
 * SQL, and `-max_item_id` in the set endpoint's.
 *
 * A set ranks by the highest id among its members rather than by its own, and
 * that is load-bearing rather than cosmetic: set ids and item ids are separate
 * numbering schemes (sets run 1..1091, items 44..34332), so a set ranked by its
 * own id sinks below every item in a merged list instead of landing beside the
 * items it is made of. `item_ids` is packed ascending by `set_row()`, so the
 * last one is the highest.
 */
function sortId(row) {
    if (isSetRow(row) && row.item_ids.length) return row.item_ids[row.item_ids.length - 1];
    return row.id;
}

/**
 * `sortValue` replaces the item field with a computed one (the damage sort reads
 * the build's stats, which the catalog knows nothing about). Direction still
 * comes from `sort_order`, so the asc/desc button keeps working either way.
 */
function comparator(sortOrder, sortValue) {
    let field = 'level';
    let direction = 'desc';
    if (sortOrder && typeof sortOrder === 'object') {
        field = sortOrder.field || sortOrder.field_name || field;
        direction = sortOrder.direction || sortOrder.order || direction;
    } else if (typeof sortOrder === 'string' && sortOrder) {
        if (sortOrder.includes('_')) {
            const [f, d] = [sortOrder.slice(0, sortOrder.lastIndexOf('_')), sortOrder.slice(sortOrder.lastIndexOf('_') + 1)];
            field = f || field;
            direction = d || direction;
        } else if (sortOrder === 'asc' || sortOrder === 'desc') {
            direction = sortOrder;
        } else {
            field = sortOrder;
        }
    }
    const sign = direction === 'asc' ? 1 : -1;
    const valueOf = typeof sortValue === 'function'
        ? (row) => sortValue(row) ?? 0
        : (row) => row[field] ?? 0;
    return (a, b) => {
        const av = valueOf(a);
        const bv = valueOf(b);
        if (av !== bv) return av < bv ? -sign : sign;
        // Ties break by id in the same direction, matching the server's
        // secondary ordering.
        const aid = sortId(a);
        const bid = sortId(b);
        if (aid !== bid) return aid < bid ? -sign : sign;
        // Ids are unique across both tables, so only a set and its own highest
        // member reach here. The server's `is_set DESC` leads with the set card,
        // in either direction.
        return (isSetRow(b) ? 1 : 0) - (isSetRow(a) ? 1 : 0);
    };
}

/**
 * Search the catalog. Returns the same `{results, total}` the API does.
 *
 * `total` is the count before paging, because the caller uses it to decide
 * whether more pages exist.
 *
 * `sortValue` orders by something the catalog does not hold — see `comparator`.
 */
export function searchItemsLocally(catalog, filters = {}, lang = 'fr', sortValue = null) {
    const {
        name,
        ids,
        item_type_ids: typeIds,
        level_min: levelMin,
        level_max: levelMax,
        stats,
        sort_order: sortOrder,
        limit = DEFAULT_LIMIT,
        offset = 0,
    } = filters;

    let candidates = catalog.items;

    if (ids !== undefined) {
        // An ids filter that sanitises to nothing must match nothing — asking for
        // one bad id must not return the whole table.
        const wanted = new Set(
            (ids || []).map((x) => Number(x)).filter((x) => Number.isFinite(x)),
        );
        candidates = wanted.size ? candidates.filter((i) => wanted.has(i.id)) : [];
    }

    const nameMatches = name ? matchesName(catalog, lang, name) : null;
    if (nameMatches !== null) {
        const keep = new Set();
        for (const idx of nameMatches) keep.add(catalog.items[idx].id);
        candidates = candidates.filter((i) => keep.has(i.id));
    }

    const typeSet = typeIds && typeIds.length ? new Set(typeIds.map(Number)) : null;
    candidates = candidates.filter((item) => {
        if (typeSet && !typeSet.has(Number(item.item_type_id))) return false;
        if (levelMin !== undefined && levelMin !== null && item.level < levelMin) return false;
        if (levelMax !== undefined && levelMax !== null && item.level > levelMax) return false;
        return passesStats(item, stats);
    });

    const total = candidates.length;
    const sorted = [...candidates].sort(comparator(sortOrder, sortValue));
    const start = Number(offset) || 0;
    const size = Number(limit) || DEFAULT_LIMIT;
    return { results: sorted.slice(start, start + size), total };
}


/** Mirrors the server's WEAPON_TYPE_IDS, which it derives from ItemTypes. */
const WEAPON_TYPE_IDS = new Set(TYPES.filter((t) => t.category === CAT.WEAPON).map((t) => t.id));

/**
 * Best value a set awards for one characteristic, across every tier.
 *
 * `bonuses_map` is keyed `"<charId>:<pieces>"`, and the server's stat filters
 * take MAX over the keys sharing a characteristic prefix — a set qualifies on
 * the best tier it can reach, not on any particular one.
 */
function bestSetBonus(bonusesMap, charId) {
    const prefix = `${charId}:`;
    let best = null;
    for (const [key, value] of Object.entries(bonusesMap || {})) {
        if (!key.startsWith(prefix)) continue;
        const n = Number(value);
        if (Number.isFinite(n) && (best === null || n > best)) best = n;
    }
    return best;
}

function setPassesStats(itemSet, stats) {
    if (!stats) return true;
    const map = itemSet.bonuses_map || {};
    for (const [charId, rules] of Object.entries(stats)) {
        if (!/^\d+$/.test(charId)) continue;
        const best = bestSetBonus(map, Number(charId));
        const present = best !== null;
        const value = present ? best : 0;
        if (rules.eq !== undefined) {
            if (rules.eq === 0 ? present && value !== 0 : !present || value !== rules.eq) return false;
        }
        if (rules.min !== undefined && value < rules.min) return false;
        // `max` uses the raw MAX with no COALESCE server-side, so a set with no
        // such bonus at all does not satisfy an upper bound.
        if (rules.max !== undefined && (!present || value > rules.max)) return false;
    }
    return true;
}

/**
 * Search sets locally, mirroring `build_set_filter_fragments`.
 *
 * The type filter is the subtle one: weapons share a slot, so several weapon
 * types are OR-ed (the set needs *a* member of any of them), while non-weapon
 * types are AND-ed (the set needs a member of each).
 */
export function searchSetsLocally(catalog, filters = {}, lang = 'fr') {
    const {
        name,
        item_type_ids: typeIds,
        level_min: levelMin,
        level_max: levelMax,
        stats,
        sort_order: sortOrder,
        limit = DEFAULT_LIMIT,
        offset = 0,
    } = filters;

    const memberTypes = (itemSet) => {
        const out = new Set();
        for (const id of itemSet.item_ids || []) {
            const item = catalog.itemById.get(id);
            if (item) out.add(Number(item.item_type_id));
        }
        return out;
    };

    let matchedSetIds = null;
    if (name) {
        // A set matches on its own name or on any member's name — the same OR the
        // server expresses with an EXISTS over core_item.
        const itemHits = matchesName(catalog, lang, name);
        const viaItems = new Set();
        if (itemHits) for (const idx of itemHits) {
            const setId = catalog.items[idx].item_set_id;
            if (setId !== null && setId !== undefined) viaItems.add(setId);
        }
        const tokens = String(name).trim().split(/\s+/).filter(Boolean).map(normalizeText);
        matchedSetIds = new Set();
        for (const s of catalog.sets) {
            const hay = normalizeText(localizedName(s.name, lang));
            if (tokens.every((t) => hay.includes(t)) || viaItems.has(s.id)) matchedSetIds.add(s.id);
        }
    }

    const weaponWanted = (typeIds || []).filter((t) => WEAPON_TYPE_IDS.has(Number(t))).map(Number);
    const otherWanted = (typeIds || []).filter((t) => !WEAPON_TYPE_IDS.has(Number(t))).map(Number);

    const matched = catalog.sets.filter((s) => {
        if (matchedSetIds && !matchedSetIds.has(s.id)) return false;
        if (levelMin !== undefined && levelMin !== null && (s.level ?? 0) < levelMin) return false;
        if (levelMax !== undefined && levelMax !== null && (s.level ?? 0) > levelMax) return false;
        if (typeIds && typeIds.length) {
            const types = memberTypes(s);
            if (!otherWanted.every((t) => types.has(t))) return false;
            if (weaponWanted.length && !weaponWanted.some((t) => types.has(t))) return false;
        }
        return setPassesStats(s, stats);
    });

    const total = matched.length;
    const sorted = [...matched].sort(comparator(sortOrder));
    const start = Number(offset) || 0;
    const size = Number(limit) || DEFAULT_LIMIT;
    // Members are ids in the catalog; the UI wants the items themselves.
    const results = sorted.slice(start, start + size).map((s) => ({
        ...s,
        items: (s.item_ids || []).map((id) => catalog.itemById.get(id)).filter(Boolean),
    }));
    return { results, total };
}


/**
 * Items and sets in one list, tagged the way the unified endpoint tags them.
 *
 * Both sides are searched unpaged and merged before slicing, because paging the
 * two independently would drop results: a page of the combined list is not the
 * concatenation of a page of each.
 */
export function searchUnifiedLocally(catalog, filters = {}, lang = 'fr', sortValue = null) {
    const unpaged = { ...filters, limit: Number.MAX_SAFE_INTEGER, offset: 0 };
    const items = searchItemsLocally(catalog, unpaged, lang, sortValue).results
        .map((item) => ({ ...item, search_result_type: 'item' }));
    const sets = searchSetsLocally(catalog, unpaged, lang).results
        .map((set) => ({ ...set, search_result_type: 'set' }));

    // Sets score 0 under a `sortValue` that only items can answer, so they land
    // at the bottom of a damage-sorted list rather than interleaving at random.
    const merged = [...items, ...sets].sort(comparator(filters.sort_order, sortValue));
    const start = Number(filters.offset) || 0;
    const size = Number(filters.limit) || DEFAULT_LIMIT;
    return { results: merged.slice(start, start + size), total: merged.length };
}
